"use client";

import React, { useEffect, useRef, useState } from "react";
import BreadCrumb from "../../Components/GlobalComponents/BreadCrumb";
import TableWrapper from "../../Components/GlobalComponents/TableWrapper";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import { canWrite, generateDarkenColor } from "src/helpers/helperFunctions";
import {
  ICategoryItems,
  IqueryParams,
  ITagsItems,
} from "src/types/GlobalInterfaces";
import { IPaginated } from "src/types/paginated";
import moment from "moment";
import eyeIcon from "../../assets/images/eye-icon.png";
import editIcon from "../../assets/images/edit-icon.png";
import deleteIcon from "../../assets/images/delete-icon.png";
import httpRequest from "src/helpers/httpRequest";
import { toast } from "react-toastify";
import QueryString from "qs";
import PrimaryBtn from "../../Components/GlobalComponents/primaryBtn";
import SimpleInput from "../../Components/SimpleInput";
import ConfirmModal from "../../Components/ConfirmModal";
import ImageUpload from "../../Components/GlobalComponents/ImageUplaod";
import ViewTagModal from "../../Components/Modals/ViewTagModal";
import ConfirmationModal from "src/Components/GlobalComponents/ConfirmationModal";

interface ICategoryResponse {
  documents: ITagsItems[];
  paginated: IPaginated;
}

interface ITagAddResponse {
  success: boolean;
  message?: string;
  name: string;
  color: string;
  createdAt: string;
  updatedAt: string;
  _id: string;
  __v: string;
  data?: {
    name: string;
    color: string;
    createdAt: string;
    updatedAt: string;
    _id: string;
    __v: string;
  };
}

interface IFormData {
  tagName: string;
  tagColor: string;
  bannerImage: File | string;
  description: string;
  imgePath?: string;
}

interface IPayloadType {
  name?: string;
  color?: string;
}

const Tags = () => {
  const { userDetails } = useSelector((store: RootState) => store.appReducer);
  const hasWriteAccess = canWrite(userDetails, "pharmaInventory", false);
  const [isUpsert, setIsUpsert] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<ITagsItems | null>(null);

  const [deleteLoader, setDeleteLoader] = useState<boolean>(false);
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [viewData, setViewData] = useState<any>(null);
  const [allCategories, setAllCategories] = useState<ITagsItems[]>([]);
  const [paginated, setPaginated] = useState<IPaginated>({
    currentPage: 1,
    totalPages: 1,
  });
  const [formData, setFormData] = useState<IFormData>({
    tagName: "",
    tagColor: "",
    bannerImage: "",
    description: "",
    imgePath: "",
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<string>("");
  const [activeTab, setActiveTab] = useState<number>(1); // Set to 2 for Product Tags

  const [queryParams, setQueryParams] = useState<IqueryParams>({
    page: 1,
    limit: 10,
    counter: 1,
  });

  const resetState = () => {
    setIsUpsert(false);
    setErrors({});
    setFormData({
      tagName: "",
      tagColor: "",
      bannerImage: "",
      description: "",
      imgePath: "",
    });
    setIsEdit(null);
  };

  useEffect(() => {
    if (isUpsert === false) {
      resetState();
    }
  }, [isUpsert]);

  const handleEdit = (obj: ITagsItems) => {
    if (!hasWriteAccess) {
      return;
    }
    setIsUpsert(true);
    setIsEdit(obj);
    if (activeTab === 1) {
      setFormData((prev) => ({
        ...prev,
        tagColor: obj?.color,
        tagName: obj?.name,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        tagName: obj?.name,
        description: obj?.description || "",
        imgePath: obj?.banner,
        bannerImage: obj?.banner,
      }));
    }
  };

  const handlePageChange = (e: number) => {
    setQueryParams((prev) => ({
      ...prev,
      page: e,
    }));
  };

  const handleDelete = (id: string) => {
    if (!hasWriteAccess) {
      return;
    }
    setDeleteModal(true);
    setDeleteId(id);
  };

  const deleteModalClose = () => {
    setDeleteModal(false);
    setDeleteId("");
  };

  const deleteCategory = async () => {
    setDeleteLoader(true);
    let pth = activeTab === 1 ? "/tags/promotional/" : "/tags/product/";
    const { res, err } = await httpRequest<any>({
      method: "delete",
      path: `${pth}${deleteId}`,
      params: {},
    });
    if (!err) {
      toast(res?.message || "Tag deleted successfully.", { type: "success" });
      setQueryParams((prev) => ({
        ...prev,
        counter: (prev.counter ?? 0) + 1,
        page:
          allCategories?.length > 1
            ? prev?.page
            : prev?.page > 1
            ? prev?.page - 1
            : prev?.page,
      }));
      setDeleteModal(false);
      setAllCategories(allCategories.filter((tm) => tm?._id !== deleteId));
      setDeleteId("");
    } else {
      toast(err, { type: "error" });
    }
    setDeleteLoader(false);
  };

  useEffect(() => {
    (async () => {
      setIsLoader(true);
      let { counter, ...remainingParams } = queryParams;
      let pth = activeTab === 1 ? "/tags/promotional/all" : "/tags/product/all";
      const { res, err } = await httpRequest<ICategoryResponse>({
        path: `${pth}?${QueryString.stringify(remainingParams)}`,
      });
      if (res) {
        setAllCategories(res?.documents);
        setPaginated(res?.paginated);
      } else {
        toast(err, { type: "error" });
      }
      setIsLoader(false);
    })();
  }, [queryParams, activeTab]);

  const setIntoErrors = (key: keyof IFormData, msg: string = "") => {
    setErrors((prev) => ({
      ...prev,
      [key]: msg,
    }));
  };

  const handleSubmit = async () => {
    let payload: IPayloadType | FormData = {};
    if (activeTab === 1) {
      if (!formData.tagName) {
        return setIntoErrors("tagName", "Please enter tag name!");
      }
      if (!formData.tagColor) {
        return setIntoErrors("tagColor", "Please pick tag color!");
      }
      payload = {
        name: formData.tagName,
        color: formData.tagColor,
      };
    } else {
      if (!formData.bannerImage && !formData?.imgePath) {
        return setIntoErrors("bannerImage", "Please select banner image!");
      }
      if (!formData.tagName) {
        return setIntoErrors("tagName", "Please enter tag name!");
      }
      if (!formData.description) {
        return setIntoErrors("description", "Please enter description!");
      }
      payload = new FormData();
      payload.append("name", formData.tagName);
      if (formData.bannerImage && formData.bannerImage instanceof File) {
        payload.append("banner", formData.bannerImage);
      }
      payload.append("description", formData.description);
    }
    setSaveLoading(true);

    let pth = activeTab === 1 ? "/tags/promotional/" : "/tags/product/";
    const method = isEdit ? "put" : "post";

    const { res, err } = await httpRequest<ITagAddResponse>({
      path: `${pth}${isEdit ? isEdit?._id : "add"}`,
      method,
      params: payload,
      header: activeTab === 2 ? { "Content-Type": "multipart/form-data" } : {}, // Set content type for FormData
    });

    if (res) {
      let { success, message, ...resData } = res;
      toast.success(message);
      let data = isEdit
        ? allCategories.map((tm) => (tm._id === res.data?._id ? res.data : tm))
        : [resData, ...allCategories];
      setAllCategories(data);
      resetState();
    } else {
      toast(err, { type: "error" });
    }
    setSaveLoading(false);
  };

  return (
    <>
      <ConfirmationModal
        show={deleteModal}
        onClose={() => setDeleteModal(false)}
        title={`Are you sure you want to delete this tag.`}
        leftBtnText="Cancel"
        rightBtnText="Delete"
        loader={deleteLoader}
        onConfirm={deleteCategory}
      />
      {viewData && (
        <ViewTagModal data={viewData} onClose={() => setViewData(null)} />
      )}
      <BreadCrumb
        current="Tags"
        previous="dashboard"
        navigateUrl="/admin"
        btnShow={true}
        btnText="Add New Tag"
        onPress={() => {
          if (!hasWriteAccess) {
            return;
          }
          setIsUpsert(!isUpsert);
        }}
        isUpsert={isUpsert}
        BtnId="add-new-tag-btn"
        btnClassName={`${
          !hasWriteAccess ? "cursor-not-allowed" : "cursor-pointer"
        }`}
      />
      <div className="table-container">
        {isUpsert ? (
          <>
            <div className="flex flex-col w-full max-w-[700px] bg-[#F6FBFF] rounded-xl p-5">
              {activeTab === 2 && (
                <>
                  <h3 className="font-size-18px text-[#2F2F31] mb-1 font-Poppins-Regular">
                    Banner Image
                  </h3>
                  <ImageUpload
                    whilePrewiev={
                      !formData?.bannerImage ? formData?.imgePath : undefined
                    }
                    image={formData?.bannerImage}
                    onImageChange={(file) => {
                      setFormData((prev) => ({
                        ...prev,
                        bannerImage: file ? file : "",
                      }));
                      setIntoErrors("bannerImage");
                    }}
                    extraClasses="w-full"
                    id="banner-image-input"
                    labelId="banner-image-label"
                  />
                  {errors?.bannerImage && (
                    <p className="font-size-15px -mt-2 font-Poppins-Regular text-red-500 ">
                      {errors?.bannerImage}
                    </p>
                  )}
                </>
              )}
              <SimpleInput
                value={formData?.tagName || ""}
                onChange={(val) => {
                  setFormData((prev) => ({ ...prev, tagName: val }));
                  setIntoErrors("tagName");
                }}
                disabled={saveLoading}
                isLabel={true}
                labelText={"Tag Name"}
                type={"text"}
                required={true}
                maxLength={30}
                id="tag-name-input"
                onEnter={() => handleSubmit()}
              />
              {errors?.tagName && (
                <p className="font-size-15px -mt-2 font-Poppins-Regular text-red-500 ">
                  {errors?.tagName}
                </p>
              )}

              {activeTab === 1 && (
                <SimpleInput
                  value={formData?.tagColor || ""}
                  onChange={(val) => {
                    setFormData((prev) => ({ ...prev, tagColor: val }));
                    setIntoErrors("tagColor");
                  }}
                  disabled={saveLoading}
                  isLabel={true}
                  labelText={"Tag Color"}
                  type={"color"}
                  id="tag-color-input"
                  pickerId="tag-color-picker"
                  onEnter={() => handleSubmit()}
                />
              )}
              {errors?.tagColor && (
                <p className="font-size-15px -mt-1 font-Poppins-Regular text-red-500">
                  {errors?.tagColor}
                </p>
              )}

              {activeTab === 2 && (
                <SimpleInput
                  value={formData?.description || ""}
                  onChange={(val) => {
                    setFormData((prev) => ({ ...prev, description: val }));
                    setIntoErrors("description");
                  }}
                  disabled={saveLoading}
                  isLabel={true}
                  labelText={"Description"}
                  rows={6}
                  type={"textArea"}
                  isLimit={300}
                  id="tag-description-input"
                  maxLength={300}
                  onEnter={() => handleSubmit()}
                />
              )}
              {errors?.description && (
                <p className="font-size-15px -mt-6 2xl:-mt-8 font-Poppins-Regular text-red-500">
                  {errors?.description}
                </p>
              )}
            </div>
            <div className="w-full max-w-[700px] mt-5 flex justify-end">
              <PrimaryBtn
                onClick={handleSubmit}
                ButtonText="Save"
                btnExtraStyle={{
                  backgroundColor: "#00443f",
                  borderRadius: "50px",
                  padding: "0.5rem 2rem",
                }}
                loader={saveLoading}
                id="save-tag-btn"
              />
            </div>
          </>
        ) : (
          <>
            <div className="flex gap-2 w-full mb-5">
              <PrimaryBtn
                onClick={() => setActiveTab(1)}
                ButtonText="Promotional Tags"
                btnExtraStyle={{
                  border: "1px solid",
                  backgroundColor: activeTab === 1 ? "#00443f" : "transparent",
                  color: activeTab === 1 ? "white" : "#00443f",
                  borderColor: activeTab === 1 ? "transparent" : "#00443f",
                  padding: "0.5rem 2rem",
                }}
                id="promotional-tags-btn"
              />
              <PrimaryBtn
                onClick={() => setActiveTab(2)}
                ButtonText="Product Tags"
                btnExtraStyle={{
                  border: "1px solid",
                  backgroundColor: activeTab === 2 ? "#00443f" : "transparent",
                  color: activeTab === 2 ? "white" : "#00443f",
                  borderColor: activeTab === 2 ? "transparent" : "#00443f",
                  padding: "0.5rem 2rem",
                }}
                id="product-tags-btn"
              />
            </div>
            <TableWrapper
              isLoading={isLoader}
              thead={["S.No", "Tags Name", "Created date", "Action"]}
              page={paginated?.currentPage || 1}
              total={paginated?.totalPages || 0}
              columnWidth="6% 70% 12% 12%"
              onChange={handlePageChange}
            >
              <div className="tbody-content-height">
                {allCategories?.map((item: ITagsItems, index: number) => (
                  <ul
                    key={index}
                    className="font-size-16px tbody-light-table font-Poppins-Regular grid"
                    style={{ gridTemplateColumns: "6% 70% 12% 12%" }}
                  >
                    <li>
                      {(paginated?.currentPage - 1) * queryParams.limit +
                        (index + 1)}
                    </li>
                    <li>{item?.name || ""}</li>
                    <li>{moment(item?.createdAt).format("DD-MM-YYYY")}</li>
                    <li className="action-last-colum">
                      {activeTab === 2 && (
                        <img
                          onClick={() => setViewData(item)}
                          src={eyeIcon}
                          alt=""
                          className={`${!hasWriteAccess ? "read_access" : ""}`}
                          id={`view-tag-${index + 1}`}
                        />
                      )}
                      <img
                        onClick={() => handleEdit(item)}
                        src={editIcon}
                        alt=""
                        className={`${!hasWriteAccess ? "read_access" : ""}`}
                        id={`edit-tag-${index + 1}`}
                      />
                      <img
                        onClick={() => handleDelete(item?._id)}
                        src={deleteIcon}
                        alt=""
                        className={`${!hasWriteAccess ? "read_access" : ""}`}
                        id={`delete-tag-${index + 1}`}
                      />
                    </li>
                  </ul>
                ))}
              </div>
            </TableWrapper>
          </>
        )}
      </div>
    </>
  );
};

export default Tags;
